import { createFileRoute, type ErrorComponentProps } from '@tanstack/react-router'
import { useState } from 'react'

import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import DataTable, { type DataTableColumn } from '@/components/ui/DataTable'
import DataTableSkeleton from '@/components/ui/DataTableSkeleton'
import EmptyState from '@/components/ui/EmptyState'
import SearchInput from '@/components/ui/SearchInput'
import Skeleton from '@/components/ui/Skeleton'
import TabBar, { type TabBarOption } from '@/components/ui/TabBar'
import TitleRow from '@/components/ui/TitleRow'
import { fundLinkOptions } from '@/features/mutual-funds/lib/links'
import RefreshRegion from '@/components/ui/RefreshRegion'
import UpdatingNote from '@/components/ui/UpdatingNote'
import { useFundList } from '@/features/mutual-funds/api/queries'
import { useOnline } from '@/hooks/useOnline'
import { lastUpdatedMeta, resolveViewState } from '@/lib/view-state'
import {
  FUND_CATEGORIES,
  fundCategorySchema,
  type Fund,
  type FundCategory,
} from '@/schemas/mutual-funds'
import CloseEndedFundTable from '@/screens/mutual-funds/components/price-vs-nav/CloseEndedFundTable'
import {
  formatMaturityDate,
  maturityLabel,
  matchesFundQuery,
} from '@/screens/mutual-funds/components/price-vs-nav/format'
import NavCell from '@/screens/mutual-funds/components/price-vs-nav/NavCell'
import NotGiven from '@/screens/mutual-funds/components/price-vs-nav/NotGiven'
import {
  FUND_COLUMN_WIDTH,
  FUND_SORT,
  FUND_TABLE,
  fundLeadColumn,
  moneyColumn,
  moneySortKey,
} from '@/screens/mutual-funds/components/shared/fund-table'
import {
  loadPriceVsNav,
  priceVsNavSearchSchema,
} from '@/screens/mutual-funds/PriceVsNavScreen.loader'

const TITLE = 'Price vs NAV'

/** Known without data, so the pending and failed pages draw the real one. */
const HEADER = <TitleRow title={TITLE} />

const CATEGORY_LABELS: Record<FundCategory, string> = {
  'close-ended': 'Close-Ended',
  'open-ended': 'Open-Ended',
  matured: 'Matured',
}
const CATEGORY_TABS: TabBarOption[] = FUND_CATEGORIES.map((value) => ({
  value,
  label: CATEGORY_LABELS[value],
}))

/**
 * A representative count for each table, close to the loaded height: close-ended has 57 funds
 * today, and that many skeleton rows is a lot of DOM for a block on screen well under a second
 * either way. `open-ended` (6) and `matured` (8) use the live count, verified against
 * dev.mmitra.space 2026-09-26 — small enough that matching it exactly costs nothing.
 */
const CLOSE_ENDED_SKELETON_ROWS = 8
const OPEN_ENDED_SKELETON_ROWS = 6
const MATURED_SKELETON_ROWS = 8

/** Each category's table: close-ended draws Fund, Fund size and Matures; open-ended Fund, Fund size and NAV; matured four. */
const SKELETON_COLUMNS: Record<FundCategory, number> = {
  'close-ended': 3,
  'open-ended': 3,
  matured: 4,
}
const SKELETON_ROW_COUNT: Record<FundCategory, number> = {
  'close-ended': CLOSE_ENDED_SKELETON_ROWS,
  'open-ended': OPEN_ENDED_SKELETON_ROWS,
  matured: MATURED_SKELETON_ROWS,
}

/** `TabBar`'s underline row: 36 tall, 20 between three labels. */
function CategoryTabsSkeleton() {
  return (
    <div className="flex h-9 items-center gap-5">
      {CATEGORY_TABS.map((tab) => (
        <Skeleton key={tab.value} className="h-5 w-24" />
      ))}
    </div>
  )
}

/**
 * The skeleton for whichever category the ENTRY url names — this route's
 * main read is keyed on `category` (docs/route-loading.md, "Search params"),
 * so this only ever renders once, for the URL the member actually landed on;
 * a tab click afterwards keeps the loaded list instead of re-running this.
 *
 * One block, as the loaded page draws it: the tabs, the search row and the table 16 apart.
 */
function PriceVsNavPending() {
  const { category } = Route.useSearch()
  return (
    <ScreenLayout pending pendingLabel="Loading price vs NAV…" header={HEADER} pageTitle={TITLE}>
      <div className="flex flex-col gap-4">
        <CategoryTabsSkeleton />
        <Skeleton className="h-11 w-full max-w-sm rounded-sm lg:h-9" />
        <DataTableSkeleton
          rows={SKELETON_ROW_COUNT[category]}
          columns={SKELETON_COLUMNS[category]}
          rowHeight="lg"
        />
      </div>
    </ScreenLayout>
  )
}

function PriceVsNavError(props: ErrorComponentProps) {
  return <RouteErrorPage {...props} header={HEADER} title="Price vs NAV did not load" />
}

export const Route = createFileRoute('/_app/mutual-funds/price-vs-nav')({
  validateSearch: priceVsNavSearchSchema,
  loader: loadPriceVsNav,
  pendingComponent: PriceVsNavPending,
  errorComponent: PriceVsNavError,
  component: PriceVsNavScreen,
})

/**
 * The mock's Open-ended table (`#/funds/price-nav?tab=open`): the fund taking the room, then Fund
 * size (160) and NAV (140, secondary, one line) against the right edge. The fund size drops out
 * on a phone-wide table rather than scrolling it.
 */
const OPEN_ENDED_COLUMNS: readonly DataTableColumn<Fund>[] = [
  fundLeadColumn<Fund>(),
  moneyColumn<Fund>({
    id: 'fundSize',
    header: 'Fund size',
    value: (row) => row.fundSize,
    width: FUND_COLUMN_WIDTH.w172,
    hideBelow: 'md',
    missing: <NotGiven />,
    plain: true,
  }),
  {
    id: 'nav',
    accessorFn: (row) => moneySortKey(row.nav),
    header: 'NAV',
    ...FUND_SORT,
    meta: { numeric: true, secondary: true, width: FUND_COLUMN_WIDTH.w140 },
    cell: ({ row }) => <NavCell nav={row.original.nav} navPeriod={row.original.navPeriod} />,
  },
]

/**
 * The mock's Matured table: Fund · Fund size (130) · Maturity date (140) · Maturity period (130),
 * the dates secondary. No NAV column: every matured fund's `nav` is null — a wound-up scheme is
 * never revalued again. No Fund manager column either (owner default Q5; the mock has none).
 * The period drops out first on a narrow table, then the date.
 */
const MATURED_COLUMNS: readonly DataTableColumn<Fund>[] = [
  fundLeadColumn<Fund>(),
  moneyColumn<Fund>({
    id: 'fundSize',
    header: 'Fund size',
    value: (row) => row.fundSize,
    width: FUND_COLUMN_WIDTH.w144,
    missing: <NotGiven />,
    plain: true,
  }),
  {
    id: 'maturedOn',
    // An ISO day sorts as its own string; as a number for the shared sort.
    accessorFn: (row) =>
      row.maturityDate === null ? undefined : Date.parse(`${row.maturityDate}T00:00:00Z`),
    header: 'Maturity date',
    ...FUND_SORT,
    meta: { numeric: true, secondary: true, width: FUND_COLUMN_WIDTH.w152, hideBelow: 'md' },
    cell: ({ row }) => formatMaturityDate(row.original.maturityDate) ?? <NotGiven />,
  },
  {
    id: 'maturityPeriod',
    accessorFn: (row) => row.maturityYears ?? undefined,
    header: 'Maturity period',
    ...FUND_SORT,
    meta: { numeric: true, secondary: true, width: FUND_COLUMN_WIDTH.w128, hideBelow: 'lg' },
    cell: ({ row }) => maturityLabel(row.original.maturityYears) ?? <NotGiven />,
  },
]

/**
 * Mutual funds · Price vs NAV — where a fund's market price sits against
 * the NAV it is actually worth, by category.
 *
 * `category` KEYS the main read, so this follows the search-param recipe
 * (docs/route-loading.md), not RankingsScreen's suspense one: `useFundList`
 * with `keepPreviousData`, a refresh region on `isPlaceholderData` while a tab
 * click's new category is still in flight — never a second skeleton for a
 * search-only change.
 *
 * The `SearchInput` filter is LOCAL STATE, not a `q` search param: each
 * category tops out at 57 rows and already arrives whole, so filtering it is
 * free client-side, and a URL `q` here would be one more field every untyped
 * `search: (prev) => …` updater in the app has to keep widening for.
 */
function PriceVsNavScreen() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const online = useOnline()
  const query = useFundList({ category: search.category })
  const view = resolveViewState(query, { online })
  const isUpdating = query.isPlaceholderData

  // The category the rows ON SCREEN actually belong to — NOT necessarily
  // `search.category`. `keepPreviousData` (in `useFundList`) keeps painting
  // the OLD category's rows the instant a tab click changes `search.category`,
  // and each category draws a different layout (the expandable close-ended
  // table vs. one of two plain table shapes): branching that layout on `search.category` while `query.data` is
  // still the old category is how a click on Matured briefly drew active
  // close-ended funds under a "Matured on" column. This only moves once
  // `isPlaceholderData` clears, i.e. once the newly-selected category's own
  // fetch has actually landed — adjusted during render (React's own pattern
  // for "keep showing the previous view a little longer") rather than an
  // effect, so the very render that receives the real data is also the one
  // that switches the layout, with no extra frame in between. The loader
  // prefetches the other two categories on a real navigation, so this window
  // is rare — a quick double-tap racing ahead of that prefetch, mostly.
  const [loadedCategory, setLoadedCategory] = useState<FundCategory>(search.category)
  if (!query.isPlaceholderData && loadedCategory !== search.category) {
    setLoadedCategory(search.category)
  }

  const [term, setTerm] = useState('')

  const rows = query.data ?? []
  const filtered = rows.filter((fund) => matchesFundQuery(fund, term))
  const searching = term.trim() !== ''

  const emptyState =
    filtered.length > 0 ? null : searching ? (
      <EmptyState kind="no-results" query={term} />
    ) : (
      <EmptyState kind="empty" title="No funds to show" />
    )

  return (
    <ScreenLayout header={HEADER} pageTitle={TITLE}>
      {/* The tabs, the search and the table are one block, 16 apart: the pages revamp's one gap
          from a tab row or a toolbar to its first block. */}
      <div className="flex flex-col gap-4">
        <TabBar
          options={CATEGORY_TABS}
          value={search.category}
          ariaLabel="Fund category"
          onChange={(value) => {
            const category = fundCategorySchema.parse(value)
            void navigate({ search: (prev) => ({ ...prev, category }) })
          }}
        />

        <div className="flex flex-wrap items-center gap-3">
          <SearchInput
            aria-label="Fund symbol, name or manager"
            placeholder="Search by symbol, name or manager"
            value={term}
            onChange={(event) => {
              setTerm(event.target.value)
            }}
            onClear={() => {
              setTerm('')
            }}
            className="max-w-sm flex-1 px-3"
          />
          <UpdatingNote when={isUpdating} label="Updating this list…" />
        </div>

        {view === 'failed' || view === 'offline' ? (
          <EmptyState
            kind={view}
            size="page"
            onRetry={() => void query.refetch()}
            retrying={query.isFetching}
            meta={view === 'failed' ? lastUpdatedMeta(query.dataUpdatedAt) : undefined}
          />
        ) : (
          <RefreshRegion updating={isUpdating}>
            {loadedCategory === 'close-ended' ? (
              view === 'loading' ? (
                // The route's `PriceVsNavPending` only ever draws for the ENTRY
                // category (docs/route-loading.md, "Search params") — this is
                // the fallback for the rare case this mounts before that
                // warmed query has landed, so a genuine first load never
                // flashes the empty-state copy below instead of a skeleton.
                <DataTableSkeleton rows={CLOSE_ENDED_SKELETON_ROWS} columns={3} rowHeight="lg" />
              ) : (
                <CloseEndedFundTable funds={filtered} emptyState={emptyState} />
              )
            ) : (
              <DataTable
                {...FUND_TABLE}
                // Keyed on the category: the two tables share no columns, and a sort clicked on
                // one must not carry over to the other.
                key={loadedCategory}
                columns={loadedCategory === 'open-ended' ? OPEN_ENDED_COLUMNS : MATURED_COLUMNS}
                rows={filtered}
                getRowId={(row) => row.symbol}
                rowLinkOptions={(row) => fundLinkOptions(row.symbol)}
                caption={
                  loadedCategory === 'open-ended'
                    ? 'Open-ended funds, fund size and NAV'
                    : 'Matured funds'
                }
                rowHeight="lg"
                isLoading={view === 'loading'}
                pageSize={
                  loadedCategory === 'open-ended' ? OPEN_ENDED_SKELETON_ROWS : MATURED_SKELETON_ROWS
                }
                emptyState={emptyState}
              />
            )}
          </RefreshRegion>
        )}
      </div>
    </ScreenLayout>
  )
}
